import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { RequestsFormValues } from '../../models';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';
import { FieldError } from './FieldError';
import { useReferences } from '../../../../../helpers/context_manager/useReferences';

type CheckboxItem = {
  key: string;
  label: string;
  hint?: string;
};

const MECHANISM_ITEMS: CheckboxItem[] = [
  { key: 'sudden_death', label: 'náhlé úmrtí' },
  { key: 'suicide', label: 'sebevražda' },
  { key: 'traffic_accident', label: 'dopravní nehoda' },
  { key: 'murder', label: 'vražda' },
  { key: 'work_injury', label: 'pracovní úraz' },
  { key: 'other_injury', label: 'jiný úraz' },
  { key: 'other', label: 'jiné' }
];

const BODY_CHANGES_ITEMS: CheckboxItem[] = [
  { key: 'advanced_autolysis', label: 'pokročilá autolýza' },
  { key: 'putrefaction', label: 'hniloba' },
  { key: 'burn', label: 'ohoření' },
  { key: 'other', label: 'jiné' }
];

const MATERIAL_LEFT: CheckboxItem[] = [
  { key: 'femoral_blood', label: 'krev femorální', hint: 'počet zkumavek' },
  { key: 'spleen_blood', label: 'krev splavy', hint: 'počet zkumavek' },
  { key: 'heart_cavity_blood', label: 'krev ze srdečních dutin', hint: 'počet zkumavek' },
  { key: 'subclavian_blood', label: 'krev podklíčová', hint: 'počet zkumavek' },
  { key: 'bladder_wash', label: 'výplach z močového měchýře' },
  { key: 'other_objects', label: 'jiné doložené předměty', hint: 'dodané léky a jiné stopy' }
];

const MATERIAL_RIGHT: CheckboxItem[] = [
  { key: 'organs', label: 'orgány' },
  { key: 'vitreous', label: 'sklivec' },
  { key: 'stomach_content', label: 'žaludeční obsah' },
  { key: 'hair', label: 'vlasy' },
  { key: 'bile', label: 'smolka' },
  { key: 'urine', label: 'moč', hint: 'ml' }
];

const REQUIRED_TESTS: CheckboxItem[] = [
  { key: 'by_measures', label: 'dle opatření' },
  { key: 'ethanol', label: 'ethanol', hint: 'stanovení hladiny pouze v krvi' },
  { key: 'extractive', label: 'extraktivní farmaka' },
  { key: 'heavy_metals', label: 'těkavé látky' },
  { key: 'cohb', label: 'COHb' },
  { key: 'just_sample', label: 'jen k zajištění' },
  { key: 'other', label: 'jiné' }
];

const fd = (path: string) => `toxicology.form_data.${path}` as const;
const tox = (path: string) => `toxicology.${path}` as const;

export const ToxicologyForm: FC = () => {
  const {
    register,
    watch,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext<RequestsFormValues>();
  const { references } = useReferences();

  const examinationTypeId = watch('examinationType');
  const selectedExamType = references.examinationTypes.find(
    (t) => String(t.id) === String(examinationTypeId)
  );
  const isSoudni = selectedExamType?.code === 'soudni';

  const mechanismOther = watch(fd('mechanism_of_death.other') as any);
  const bodyOther = watch(fd('body_changes.other') as any);
  const requiredOther = watch(fd('required_tests.other') as any);
  const materialOtherObjects = watch(fd('material.other_objects.enabled') as any);

  return (
    <Wrapper>
      <Row>
        <Col md={12}>
          <Grid2>
            {isSoudni && (
              <>
                <LabelCol>Toxikolog *</LabelCol>
                <div>
                  <ToxicologistSelect
                    $hasError={!!errors.fnbToxicologist}
                    {...register('fnbToxicologist', { required: isSoudni ? 'Povinné pole' : false })}>
                    {references.toxicologists
                      .filter((t) => t.employee)
                      .map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.employee.name}
                        </option>
                      ))}
                  </ToxicologistSelect>
                  <FieldError error={errors.fnbToxicologist} />
                </div>
              </>
            )}
            <LabelCol>Mechanismus úmrtí</LabelCol>
            <ItemsRow>
              {MECHANISM_ITEMS.map((i) => (
                <CheckItem key={i.key}>
                  <input type="checkbox" {...register(fd(`mechanism_of_death.${i.key}`) as any)} />
                  <span>{i.label}</span>
                </CheckItem>
              ))}
            </ItemsRow>

            <LabelCol>Změny na těle zemřelého</LabelCol>
            <ItemsRow>
              {BODY_CHANGES_ITEMS.map((i) => (
                <CheckItem key={i.key}>
                  <input type="checkbox" {...register(fd(`body_changes.${i.key}`) as any)} />
                  <span>{i.label}</span>
                </CheckItem>
              ))}
            </ItemsRow>

            <LabelCol>Okolnosti úmrtí, známá medikace</LabelCol>
            <WideTextarea {...register(tox('circumstances_note') as any)} />

            <LabelCol>Podezření z intoxikace</LabelCol>
            <RadioRow>
              <RadioItem>
                <input
                  type="radio"
                  value="NE"
                  {...register(tox('intoxication_suspicion') as any)}
                />
                <span>NE</span>
              </RadioItem>
              <RadioItem>
                <input
                  type="radio"
                  value="ANO"
                  {...register(tox('intoxication_suspicion') as any)}
                />
                <span>ANO</span>
              </RadioItem>
              <RadioItem>
                <input
                  type="radio"
                  value="NEJEDNOZNACNE"
                  {...register(tox('intoxication_suspicion') as any)}
                />
                <span>Nejednoznačný nález</span>
              </RadioItem>
            </RadioRow>
          </Grid2>
        </Col>
      </Row>

      <SectionDivider />

      <Row>
        <Col md={12}>
          <SectionTitle>Materiál odebraný k vyšetření</SectionTitle>
        </Col>
      </Row>

      <Row>
        <Col xl={6} lg={12} md={12}>
          {MATERIAL_LEFT.map((i) => {
            const enabledName = `material.${i.key}.enabled`;
            const amountName = `material.${i.key}.amount`;
            const textName = `material.${i.key}.text`;

            return (
              <MaterialRow key={i.key}>
                <CheckItem>
                  <input type="checkbox" {...register(fd(enabledName) as any)}
                         onChange={(e) => {
                           const enabled = e.target.checked;
                           setValue(fd(amountName), enabled ? 1 : '');
                         }}
                  />
                  <span>{i.label}</span>
                </CheckItem>

                {i.hint === 'počet zkumavek' && (
                  <MaterialInline>
                    <SmallLabel>počet zkumavek:</SmallLabel>
                    <AmountGroup>
                      <SmallInput
                        type="number"
                        min="0"
                        {...register(fd(amountName) as any, {
                          validate: (v: any) =>
                            !getValues(fd(enabledName) as any) ||
                            (v !== null && v !== '' && Number(v) >= 1) ||
                            'Zadejte množství (≥ 1)'
                        })}
                        onChange={(e) => {
                          const value = e.target.value;
                          setValue(fd(enabledName), !(!value || Number(value) < 1));
                        }}
                      />
                      <FieldError
                        error={(errors as any)?.toxicology?.form_data?.material?.[i.key]?.amount}
                      />
                    </AmountGroup>
                  </MaterialInline>
                )}

                {i.hint === 'ml' && (
                  <MaterialInline>
                    <AmountGroup>
                      <SmallInput
                        type="number"
                        min="0"
                        {...register(fd(amountName) as any, {
                          validate: (v: any) =>
                            !getValues(fd(enabledName) as any) ||
                            (v !== null && v !== '' && Number(v) >= 1) ||
                            'Zadejte množství (≥ 1)'
                        })}
                        onChange={(e) => {
                          const value = e.target.value;
                          setValue(fd(enabledName), !(!value || Number(value) < 1));
                        }}
                      />
                      <FieldError
                        error={(errors as any)?.toxicology?.form_data?.material?.[i.key]?.amount}
                      />
                    </AmountGroup>
                    <SmallLabel>ml</SmallLabel>
                  </MaterialInline>
                )}

                {i.key === 'other_objects' && materialOtherObjects && (
                  <FullWidthBlock>
                    <MaterialNoteInput
                      type="text"
                      {...register(fd(textName) as any, {
                        validate: (v: any) =>
                          !getValues(fd('material.other_objects.enabled') as any) ||
                          !!v?.trim() ||
                          'Popis materiálu je povinný'
                      })}
                    />
                    <FieldError
                      error={(errors as any)?.toxicology?.form_data?.material?.other_objects?.text}
                    />
                  </FullWidthBlock>
                )}
              </MaterialRow>
            );
          })}
        </Col>

        <Col xl={6} lg={12} md={12}>
          {MATERIAL_RIGHT.map((i) => {
            const enabledName = `material.${i.key}.enabled`;
            const amountName = `material.${i.key}.amount`;

            return (
              <MaterialRow key={i.key}>
                <CheckItem>
                  <input type="checkbox" {...register(fd(enabledName) as any)} />
                  <span>{i.label}</span>
                  {i.hint === 'ml' && (
                    <MaterialInline>
                      <SmallInput
                        type="number"
                        min="0.01"
                        step="0.01"
                        {...register(fd(amountName) as any, {
                          validate: (v: any) =>
                            !getValues(fd(enabledName) as any) ||
                            (v !== null && v !== '' && Number(v) >= 0.01) ||
                            'Zadejte množství (≥ 0.01)'
                        })}
                      />
                      <SmallLabel>ml</SmallLabel>
                    </MaterialInline>
                  )}
                </CheckItem>
                {i.hint === 'ml' && (
                  <FieldError
                    error={(errors as any)?.toxicology?.form_data?.material?.[i.key]?.amount}
                  />
                )}
              </MaterialRow>
            );
          })}
        </Col>
      </Row>

      <SectionDivider />

      <Row>
        <Col md={12}>
          <SectionTitle>Požadované vyšetření</SectionTitle>
          <RequiredGrid>
            {REQUIRED_TESTS.map((i) => (
              <RequiredItem key={i.key}>
                <CheckItem>
                  <input type="checkbox" {...register(fd(`required_tests.${i.key}`) as any)} />
                  <span>{i.label}</span>
                </CheckItem>

                {i.hint && <Hint>{i.hint}</Hint>}

                {i.key === 'other' && requiredOther && (
                  <>
                    <MaterialNoteInput
                      type="text"
                      {...register(fd('required_tests.other_text') as any, {
                        validate: (v: any) =>
                          !getValues(fd('required_tests.other') as any) ||
                          !!v?.trim() ||
                          'Upřesnění je povinné'
                      })}
                    />
                    <FieldError
                      error={(errors as any)?.toxicology?.form_data?.required_tests?.other_text}
                    />
                  </>
                )}
              </RequiredItem>
            ))}
          </RequiredGrid>
        </Col>
      </Row>

      <SectionDivider />

      <Row>
        <Col md={12}>
          <SectionTitle>Poznámka</SectionTitle>
          <BottomTextarea {...register(tox('note') as any)} />
        </Col>
      </Row>

      {(mechanismOther || bodyOther) && <Spacer />}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(3)};
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: ${Spacing(3)} ${Spacing(4)};
  align-items: start;
`;

const LabelCol = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
`;

const ItemsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing(3)} ${Spacing(6)};
  align-items: center;
`;

const CheckItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
  }

  span {
    color: ${Colors.grey};
  }
`;

const WideTextarea = styled.textarea`
  grid-column: 2 / 3;
  width: 100%;
  min-height: 72px;
  border: 1px solid ${Colors.lightGrey};
  padding: ${Spacing(2)};
  resize: vertical;
`;

const RadioRow = styled.div`
  display: flex;
  gap: ${Spacing(6)};
  flex-wrap: wrap;

  input[type='radio'] {
    width: 18px;
    height: 18px;
  }
`;

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;

  span {
    color: ${Colors.grey};
  }
`;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${Spacing(2)};
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid ${Colors.lightGrey};
  margin: ${Spacing(3)} 0;
`;

const MaterialRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${Spacing(2)};
  align-items: center;
  margin-bottom: ${Spacing(2)};
  max-width: 420px;
`;

const MaterialInline = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing(2)};
`;

const SmallLabel = styled.div`
  color: ${Colors.grey};
  font-size: 13px;
  white-space: nowrap;
`;

const SmallInput = styled.input`
  width: 70px;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
`;

const MaterialNoteInput = styled.input`
  grid-column: 1 / -1;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
`;

const FullWidthBlock = styled.div`
  grid-column: 1 / -1;
`;

const AmountGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const RequiredGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: ${Spacing(3)} ${Spacing(6)};

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RequiredItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(1)};
`;

const Hint = styled.div`
  color: ${Colors.grey};
  font-size: 12px;
  margin-left: 28px;
`;

const BottomTextarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  border: 1px solid ${Colors.lightGrey};
  padding: ${Spacing(2)};
  resize: vertical;
`;

const Spacer = styled.div``;

const ToxicologistSelect = styled.select<{ $hasError?: boolean }>`
  display: block;
  width: 100%;
  padding: 6px 10px;
  border: 1px solid ${({ $hasError }) => ($hasError ? '#dc3545' : '#ced4da')};
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${({ $hasError }) => ($hasError ? '#dc3545' : '#80bdff')};
    box-shadow: 0 0 0 2px
      ${({ $hasError }) => ($hasError ? 'rgba(220,53,69,0.25)' : 'rgba(0,123,255,0.25)')};
  }
`;
